import { combineReducers, Reducer } from 'redux';
import { RouterState } from 'redux-first-history';

import { createResponsiveStateReducer } from 'redux-responsive';
import { ThemeBreakpoints } from '@dev/ee-components';

import { account } from './account/reducer';
import { AccountState } from './account/types';

import { SessionState } from './session/types';
import { session } from './session/reducer';

import { toast } from './toast/reducer';
import { ToastState } from './toast/types';

import { rawEditor } from './raweditor/reducer';
import { RawEditorState } from './raweditor/types';

import { dialog } from './dialog/reducer';
import { DialogState } from './dialog/types';

import { upload } from './upload/reducer';
import { UploadState } from './upload/types';

import { userFiles } from './userfiles/reducer';
import { UserFilesState } from './userfiles/types';

import { DomainState } from './domain/types';

import { ValidEmailsState } from './validemails/types';
import { security } from './security/reducer';
import { SecurityState } from './security/types';

import { statistics } from './statistics/reducer';
import { StatisticsState } from './statistics/types';

import { logs } from './logs/reducer';
import { LogsState } from './logs/types';

import { channels } from './channels/reducer';
import { StatisticsChannelsState } from './channels/types';

import { config } from './config/reducer';
import { ConfigState } from './config/types';

import { SuppressionsState } from './suppressions/types';
import { suppressions } from './suppressions/reducer';

import { PoolsState } from './pools/types';
import { pools } from './pools/reducer';

import { InboundState } from './inboundRoutes/types';
import { inbound } from './inboundRoutes/reducer';

import { WebhooksState } from './webhooks/types';
import { webhooks } from './webhooks/reducer';

import { templateTagList } from './templateTagList/reducer';
import { TemplateTagListState } from './templateTagList/types';

import { emails } from './emails/reducer';
import { EmailsState } from './emails/types';

import { exports } from './exports/reducer';
import { ExportsState } from './exports/types';

import { mediaManager } from './mediaManager/reducer';
import { MediaManagerState } from './mediaManager/types';

import { products } from './products/reducer';
import { ProductListState } from './products/types';

import { reports } from './reports/reducer';
import { ReportsState } from './reports/types';

import { NotificationStore } from './notifications/types';
import { notification } from './notifications/reducer';

import { locationHistory } from './locationHistory/reducer';
import { LocationHistoryState } from './locationHistory/types';

import { deviceDetails } from './deviceDetails/reducer';
import { DeviceDetailsState } from './deviceDetails/types';

import { legal } from './legal/reducer';
import { LegalState } from './legal/types';

import { integrations } from './integrations/reducer';
import { IntegrationsState } from './integrations/types';

import { PaymentsState } from './payments/types';
import { payments } from './payments/reducer';

import { DashboardSettingsState } from './dashboardSettings/types';

import singleLeads from 'evsApp/singleLeads/reducer';
import verificationFiles from 'evsApp/verificationFiles/reducer';

import * as MarketingReducers from '../dashboards/Marketing/reducers';
import { reduceMultiProductReducers } from '@shared-store/shared-store';
import { DashboardSeenNotificationsState } from './dashboardSeenNotifications/types';
import { CustomFieldsInterface } from './customFields/types';

export interface ApplicationState {
    account?: AccountState;
    dashboardSettings?: DashboardSettingsState;
    channels?: StatisticsChannelsState;
    session?: SessionState;
    browser?: BrowserInterface;
    toast?: ToastState;
    rawEditor?: RawEditorState;
    templateList?: unknown;
    templateTagList?: TemplateTagListState;
    dialog?: DialogState;
    upload?: UploadState;
    userFiles?: UserFilesState;
    domain?: DomainState;
    validEmails?: ValidEmailsState;
    security?: SecurityState;
    statistics?: StatisticsState;
    logs?: LogsState;
    config: ConfigState;
    suppressions?: SuppressionsState;
    pools?: PoolsState;
    inbound?: InboundState;
    webhooks?: WebhooksState;
    router?: RouterState;
    emails?: EmailsState;
    exports?: ExportsState;
    mediaManager?: MediaManagerState;
    products?: ProductListState;
    reports?: ReportsState;
    notification?: NotificationStore;
    locationHistory?: LocationHistoryState;
    legal: LegalState;
    payments: PaymentsState;
    campaigns?: any;
    deviceDetails?: DeviceDetailsState;
    singleLeads: any;
    verificationFiles: any;
    formList?: any;
    contacts?: any;
    lists?: any;
    segments?: any;
    onboardingSurvey?: any;
    customFields?: CustomFieldsInterface;
    integrations?: IntegrationsState;
    dashboardSeenNotifications?: DashboardSeenNotificationsState;
}

export interface BrowserInterface {
    mediaType: string;
    orientation: string;
    lessThan: {
        xs: boolean;
        sm: boolean;
        hd: boolean;
        md: boolean;
        lg: boolean;
        xl: boolean;
    };
    greaterThan: {
        xs: boolean;
        sm: boolean;
        hd: boolean;
        md: boolean;
        lg: boolean;
        xl: boolean;
    };
    is: {
        xs: boolean;
        sm: boolean;
        hd: boolean;
        md: boolean;
        lg: boolean;
        xl: boolean;
    };
}

export const { xl, basicContainer, container, hd, ...screenSizes } = ThemeBreakpoints;

export const reducers = (routerReducer: Reducer<RouterState>): Reducer<ApplicationState> => {
    const productReducers = {
        account,
        channels,
        session,
        toast,
        templateTagList,
        dialog,
        upload,
        userFiles,
        security,
        statistics,
        logs,
        config,
        suppressions,
        pools,
        inbound,
        webhooks,
        emails,
        exports,
        mediaManager,
        products,
        browser: createResponsiveStateReducer(screenSizes as any, { infinity: 'xl' }),
        router: routerReducer,
        reports,
        notification,
        locationHistory,
        deviceDetails,
        legal,
        payments,
        rawEditor,
        integrations,
    };

    const EvsReducers = { singleLeads, verificationFiles };
    return combineReducers<ApplicationState>(
        reduceMultiProductReducers<ApplicationState>(productReducers, { ...MarketingReducers, ...EvsReducers })
    );
};

export const getRouterStore = (store: ApplicationState): RouterState => store.router;
export const getConfigStore = (store: ApplicationState): ConfigState => store.config;
export const getBrowserStore = (store: ApplicationState): BrowserInterface => store.browser;
